import React from 'react'
import { withGoogleMap, WithGoogleMapProps, GoogleMap, Marker, InfoWindow } from 'react-google-maps'
import MediaQuery from 'react-responsive'

import GoogleMapStyle from '../../constants/google_map_style'
import { propertyMarker, propertyHighlightedMarker } from '../../constants/map_marker_icons'

import Property, { PropertyInfo, PropertyPath } from '../property'
import { PropertyData } from '../../models/properties' 
import { PropertyInfoWindow } from './overlays'

interface DestinationsGoogleMapProps extends WithGoogleMapProps {
  onMapMounted: (map: any) => void
  onZoomChanged: (zoom?: any) => void
  onBoundsChanged: () => void
  zoom: number
  center: any

  onToggleOpen: (PropertyData) => void
  markers: PropertyData[]
  open: number
  guest: boolean
  compactInfo: boolean
  focalProperty?: PropertyData
  actionName?: string
  onAction?: (PropertyData) => void
  fadedMarkers?: boolean
}

const DestinationsGoogleMap = withGoogleMap((props: DestinationsGoogleMapProps) => {
  return (<GoogleMap
    ref={props.onMapMounted}
    zoom={props.zoom}
    center={props.center}
    onZoomChanged={props.onZoomChanged}
    onBoundsChanged={props.onBoundsChanged}
    defaultOptions={{
      streetViewControl: false,
      scaleControl: false,
      mapTypeControl: false,
      panControl: false,
      zoomControl: true,
      styles: GoogleMapStyle as any,
      zoomControlOptions: {
        position: google.maps.ControlPosition.LEFT_BOTTOM
      },
      rotateControl: false,
      fullscreenControl: false
    }}
  >
    { props.markers.map((p, i) => {
        let toggleOpen = () => { props.onToggleOpen(p) }

        return (
          <Marker
            key={p.id}
            position={{ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }} 
            onClick={toggleOpen}
            icon={propertyMarker(p.type_slug)}
            opacity={props.fadedMarkers ? 0.5 : 1.0}
          >
            { props.open == p.id ?
              <MediaQuery minWidth={800}>
                <InfoWindow onCloseClick={toggleOpen}>
                  <PropertyInfoWindow compact={props.compactInfo} property={p} guest={props.guest} actionName={props.actionName} onAction={props.onAction} />
                </InfoWindow>
              </MediaQuery> : undefined }
          </Marker>
        )
    }) }

    { props.focalProperty ? 
      <Marker
        position={{ lat: parseFloat(props.focalProperty.lat), lng: parseFloat(props.focalProperty.lng) }}
        icon={propertyHighlightedMarker(props.focalProperty.type_slug)}
        onClick={() => { props.onToggleOpen(props.focalProperty) }}
        zIndex={1000}
      >
        { props.open == props.focalProperty.id ?
          <MediaQuery minWidth={800}>
            <InfoWindow onCloseClick={() => { props.onToggleOpen(props.focalProperty) }}>
              <PropertyInfoWindow compact={props.compactInfo} property={props.focalProperty} guest={props.guest} actionName={props.actionName} onAction={props.onAction} />
            </InfoWindow>
          </MediaQuery> : undefined }
      </Marker>
    : undefined }
  </GoogleMap>)
})

interface PropertyMapProps {
  properties: PropertyData[]
  guest?: boolean
  focalProperty?: PropertyData
  className?: string
  defaultZoom?: number
  compactInfo?: boolean
  fadedMarkers?: boolean
  actionName?: string
  onAction?: (PropertyData) => void

  onPropertySelected?: (PropertyData) => void
}

interface PropertyMapState {
  bounds: any
  center: {
    lat: number
    lng: number
  }
  zoom: number
  infoWindow: number
}

export default class PropertyMap extends React.Component<PropertyMapProps, PropertyMapState> {
  _map: any

  static get defaultProps() {
    return {
      properties: [],
      guest: false,
      fadedMarkers: false,
      defaultZoom: 3,
      compactInfo: false,
      onPropertySelected: () => {}
    }
  }

  constructor(props) {
    super(props)

    this.calculateBoundsAndCenter = this.calculateBoundsAndCenter.bind(this)
    this.handleMapMounted = this.handleMapMounted.bind(this)
    this.handleBoundsChanged = this.handleBoundsChanged.bind(this)
    this.handleZoomChanged = this.handleZoomChanged.bind(this)
    this.toggleInfoWindow = this.toggleInfoWindow.bind(this)

    let initialState = Object.assign({}, {
      bounds: null,
      center: { lat: 0, lng: 0 },
      zoom: props.defaultZoom,
      infoWindow: 0
    }, this.calculateBoundsAndCenter(props.properties))

    this.state = initialState
  }

  calculateBoundsAndCenter(properties) {
    if (properties.length < 1 && !this.props.focalProperty) {
      return { center: { lat: 51.810337, lng: -1.636715 }, zoom: 3 }
    }

    var bounds = new google.maps.LatLngBounds();
    for (var i = 0; i < properties.length; i++) {
      const p = properties[i]
      bounds.extend({
        lat: parseFloat(p.lat),
        lng: parseFloat(p.lng)
      });
    }

    var calculations: any = {
      bounds: bounds,
      center: bounds.getCenter()
    }

    // Enforce a minimum zoom level if only one property is shown
    if (properties.length == 1) {
      calculations.zoom = 8
      if (this._map && this._map.getZoom() == calculations.zoom) {
        // Bodgy, but use another zoom level purely to trigger a redraw
        calculations.zoom = this._map.getZoom() - 1
      }
    }

    if (this.props.focalProperty) {
      const property = this.props.focalProperty
      calculations.center = { lat: parseFloat(property.lat), lng: parseFloat(property.lng) }

      // Ensure without doubt the focalProperty is centred
      if (this._map) {
        this._map.panTo(calculations.center)
      }
    } else {
      if (this._map && properties.length > 1) {
        this._map.fitBounds(calculations.bounds, 5)
      }
    }

    return calculations
  }

  componentWillReceiveProps(newProps) {
    this.setState(this.calculateBoundsAndCenter(newProps.properties))
  }

  handleZoomChanged(zoom) {
    this.setState({
      zoom: this._map.getZoom()
    })
  }

  handleMapMounted(map) {
    this._map = map
    if (this._map) {
      if (this.props.focalProperty) {
        this._map.panTo(this.state.center)
      } else if (this.props.properties.length > 1) {
        this._map.fitBounds(this.state.bounds, 5)
      }
    }
  }

  handleBoundsChanged() {
    this.setState({
      bounds: this._map.getBounds()
    })
  }

  toggleInfoWindow(property) {
    if (this.state.infoWindow != property.id) {
      this.setState({
        infoWindow: property.id
      }, () => {
        this.props.onPropertySelected(property)
      })
    } else {
      this.setState({ infoWindow: 0 })
    }
  }

  render() {
    const { properties, guest, focalProperty, compactInfo, actionName, onAction, fadedMarkers } = this.props
    const { zoom, bounds, center, infoWindow } = this.state

    return (
      <DestinationsGoogleMap
        ref="map"
        containerElement={
          <div style={{ width: `100%`, height: `100%` }} />
        }
        mapElement={
          <div style={{ width: `100%`, height: `100%` }} />
        }
        onMapMounted={this.handleMapMounted}
        onBoundsChanged={this.handleBoundsChanged}
        onZoomChanged={this.handleZoomChanged}
        onToggleOpen={this.toggleInfoWindow}
        open={infoWindow}
        center={center}
        zoom={zoom}
        markers={properties}
        focalProperty={focalProperty}
        guest={guest}
        compactInfo={compactInfo}
        actionName={actionName}
        onAction={onAction}
        fadedMarkers={fadedMarkers}
      />
    )
  }
}